import React, { useState, useEffect, useCallback } from 'react'
import { Classes, Dialog, InputGroup } from "@blueprintjs/core"
import { observer } from "mobx-react"
import axios from 'axios'
import { PlanrButton } from 'modules/common/components/planr/button/Button'
import { Buttons } from "modules/common/components/form"
import { FileLink } from "modules/common/components/files/FileLink"
import { LocalLoader } from "modules/common/components/loader/Loader"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { PendingSignatureItemType, SignatureDtoType } from "modules/main/models/order-content-task-signatures"
import { describeCryptoError, useCryptoPro } from './useCryptoPro'
import { apiUrls } from 'modules/common/services/communication/urls'
import { eat } from "modules/common/services/typescript"
import styles from "./CryptoDialog.module.scss"
import { DefaultSelectedOption, filterItemPredicate, renderSingleOption, SimpleSelect } from 'modules/common/services/form/select'
import { Constants } from 'modules/root/models/constants'
import { AttachSignatureForm, AttachSignatureFormState } from './AttachSignatureForm'

interface SignDialogProps {
  isOpen: boolean
  selectedSignature: PendingSignatureItemType | null
  onClose: () => void
  onSubmitSignature: (
    signature: PendingSignatureItemType,
    thumbprint: string,
    signatureData: string,
    rowVersion: number  // Добавляем rowVersion
  ) => Promise<void | null>
  onGetSignature: (id: string, signatureSource: string) => Promise<SignatureDtoType>
  onNotifyError: (error: string) => void
  onNotifySuccess?: (message: string) => void
  baseUrl: string
  signaturesToSignAll?: PendingSignatureItemType[] | null
  selectedSignerName?: string | null
  /**
   * Подписание за другого человека: кроме выбора сертификата можно прикрепить
   * готовый файл подписи стороннего подписанта (.sig + файл результата)
   */
  allowAttach?: boolean
  /** Режим при открытии (для аутсорсера логичнее сразу «Файлом подписи») */
  defaultMode?: SignMode
  onAttachSignature?: (signature: PendingSignatureItemType, signatureData: string, signerNames: string[]) => Promise<void>
}

export type SignMode = 'cert' | 'attach'


export const SignDialog: React.FC<SignDialogProps> = observer(({
  isOpen,
  selectedSignature,
  onClose,
  onSubmitSignature,
  onGetSignature,
  onNotifyError,
  baseUrl, signaturesToSignAll, onNotifySuccess, selectedSignerName, allowAttach, defaultMode, onAttachSignature
}) => {
  const { certs, fetchCerts, signBase64, loading: cryptoLoading, error: cryptoError } = useCryptoPro()
  const [selectedThumbprint, setSelectedThumbprint] = useState<string>('')
  const [isSigning, setIsSigning] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState<string>('')
  const [comment, setComment] = useState<string>('')
  const [selectedType, setSelectedType] = useState<SelectItem | null>(null)

  const [selectedRole, setSelectedRole] = useState<SelectItem | null>(null)
  const [availableRoles, setAvailableRoles] = useState<SelectItem[]>([])

  // Режим подписания за другого: своим сертификатом или файлом подписи стороннего подписанта
  const [mode, setMode] = useState<SignMode>('cert')
  const [attachForm, setAttachForm] = useState<AttachSignatureFormState>({ sigBase64: null, hashMatch: false, signerNames: [] })
  const [attachFormKey, setAttachFormKey] = useState(0)
  const [isAttaching, setIsAttaching] = useState(false)
  const handleAttachFormChange = useCallback((state: AttachSignatureFormState) => setAttachForm(state), [])

  // Заполняем доступные роли из signaturesToSignAll
  useEffect(() => {
    if (signaturesToSignAll && signaturesToSignAll.length > 0) {
      // Получаем уникальные роли
      const uniqueRoles = new Map<string, string>()
      signaturesToSignAll.forEach((sig) => {
        if (sig.signerType && sig.signerTypeDisplay) {
          uniqueRoles.set(sig.signerType, sig.signerTypeDisplay)
        }
      })

      const roles: SelectItem[] = [
        { id: 'all', label: 'Все' }, // Первый элемент "Все"
        ...Array.from(uniqueRoles.entries()).map(([id, label]) => ({
          id,
          label
        }))
      ]

      setAvailableRoles(roles)
      setSelectedRole(roles[0]) // По умолчанию выбираем "Все"
    }
  }, [signaturesToSignAll])

  // Обновляем комментарий при выборе роли
  useEffect(() => {
    if (selectedRole && selectedRole.id !== 'all') {
      setComment(selectedRole.label)
    } else if (selectedRole?.id === 'all') {
      setComment('')
    }
  }, [selectedRole])


  // Загружаем сертификаты при открытии диалога
  useEffect(() => {
    if (isOpen) {
      fetchCerts()
      setSelectedThumbprint('')
      setMode(defaultMode || 'cert')
    }
  }, [isOpen, fetchCerts, defaultMode])

  // Сброс состояния при закрытии
  useEffect(() => {
    if (!isOpen) {
      setSelectedThumbprint('')
      setIsSigning(false)
      setDownloadProgress('')
      setMode('cert')
      setAttachForm({ sigBase64: null, hashMatch: false, signerNames: [] })
      setAttachFormKey((key) => key + 1)
      setIsAttaching(false)
    }
  }, [isOpen])

  const handleAttach = async () => {
    if (!selectedSignature || !onAttachSignature) return

    if (!attachForm.sigBase64) {
      onNotifyError('Необходимо выбрать файл подписи (.sig)')
      return
    }

    if (!attachForm.hashMatch) {
      onNotifyError('Сначала проверьте хэш файла результата. Он должен совпадать с файлом в базе.')
      return
    }

    try {
      setIsAttaching(true)
      await onAttachSignature(selectedSignature, attachForm.sigBase64, attachForm.signerNames)
      onNotifySuccess?.('Подпись успешно прикреплена')
      onClose()
    } catch (error) {
      onNotifyError(`Ошибка при загрузке подписи: ${(error as any).message}`)
    } finally {
      setIsAttaching(false)
    }
  }


  useEffect(() => {
    if (selectedSignature?.signerTypeDisplay) {
      setComment(selectedSignature.signerTypeDisplay)
    } else {
      setComment('')
    }
  }, [selectedSignature])

  const getFileUrl = (): string => {
    if (!selectedSignature) return ''
    return baseUrl + apiUrls.application.files.content(selectedSignature.fileId, selectedSignature.fileName)
  }
  // const formatBytes = (bytes: number, decimals: number = 2): string => {
  //   if (bytes === 0) return '0 Bytes'

  //   const k = 1024
  //   const dm = decimals < 0 ? 0 : decimals
  //   const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  //   const i = Math.floor(Math.log(bytes) / Math.log(k))

  //   return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
  // }
  const handleSign = async () => {
    if (!selectedSignature || !selectedThumbprint) return

    try {
      setIsSigning(true)
      setDownloadProgress('Получение содержимого файла из базы данных...')

      // 1. Скачиваем файл
      const fileUrl = getFileUrl()
      const response = await axios.get(fileUrl, {
        responseType: 'arraybuffer',
        withCredentials: true
      })

      setDownloadProgress('Файл загружен, проверка наличия подписей...')

      const currentSignature = await onGetSignature(selectedSignature.signatureId, selectedSignature.signatureSource)
      // 2. Подписываем открепленной подписью
      setDownloadProgress('Подписание документа...')
      const signature = await signBase64(selectedThumbprint,
        response.data,
        // allChunks.buffer,
        currentSignature.signature, selectedType?.id, comment)



      // if (signature) {

      //   // Получаем имя файла из URL или заголовков
      //   let fileName = 'document'
      //   const contentDisposition = response.headers['content-disposition']
      //   if (contentDisposition) {
      //     const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      //     if (fileNameMatch && fileNameMatch[1]) {
      //       fileName = fileNameMatch[1].replace(/['"]/g, '')
      //     }
      //   } else {
      //     // Если нет Content-Disposition, извлекаем из URL
      //     const urlParts = fileUrl.split('/')
      //     fileName = urlParts[urlParts.length - 1].split('?')[0] || 'document'
      //   }
      //   // Создаем blob из подписи (подпись уже в base64)
      //   const signatureBlob = new Blob([signature], { type: 'application/pkcs7-signature' })

      //   // Создаем имя файла подписи (исходное имя + .sig)
      //   const signatureFileName = `${fileName}.sig`

      //   // Создаем ссылку для скачивания
      //   const signatureUrl = window.URL.createObjectURL(signatureBlob)
      //   const link = document.createElement('a')
      //   link.href = signatureUrl
      //   link.download = signatureFileName

      //   // Добавляем ссылку в DOM, кликаем и удаляем
      //   document.body.appendChild(link)
      //   link.click()
      //   document.body.removeChild(link)

      //   // Освобождаем URL
      //   window.URL.revokeObjectURL(signatureUrl)
      // }
      // 3. Отправляем подпись через callback в List
      if (signature) {
        setDownloadProgress('Отправка подписи на сервер...')
        await onSubmitSignature(selectedSignature, selectedThumbprint, signature, currentSignature.rowVersion)
        onClose()
      }

    } catch (e) {
      console.info('Ошибка подписания:', e)
      onNotifyError(`Ошибка подписания: ${describeCryptoError(e)}`)
    } finally {
      setIsSigning(false)
      setDownloadProgress('')
    }
  }



  const handleSignAll = async () => {
    const signaturesToProcess = getFilteredSignatures()

    if (!signaturesToProcess || signaturesToProcess.length === 0 || !selectedThumbprint) {
      if (signaturesToProcess.length === 0) {
        onNotifyError('Нет документов с выбранной ролью для подписи')
      }
      return
    }

    setIsSigning(true)
    let successCount = 0
    let errorCount = 0
    let versionErrorCount = 0

    try {
      setDownloadProgress('Скачивание файлов...')

      // Дедупликация по fileId
      const uniqueFiles = new Map<string, PendingSignatureItemType>()
      for (const signature of signaturesToProcess) {
        if (!uniqueFiles.has(signature.fileId)) {
          uniqueFiles.set(signature.fileId, signature)
        }
      }

      // Параллельное скачивание уникальных файлов 
      const downloadPromises = Array.from(uniqueFiles.entries()).map(async ([fileId, signature]) => {
        const fileUrl = baseUrl + apiUrls.application.files.content(signature.fileId, signature.fileName)
        const response = await axios.get(fileUrl, {
          responseType: 'arraybuffer',
          withCredentials: true
        })
        return { fileId, fileData: response.data }
      })

      const downloadedFiles = await Promise.all(downloadPromises)

      const fileDataMap = new Map<string, any>()
      for (const item of downloadedFiles) {
        fileDataMap.set(item.fileId, item.fileData)
      }

      setDownloadProgress(`Подписание документов...`)

      // Все делаем последовательно
      for (let i = 0; i < signaturesToProcess.length; i++) {
        const signature = signaturesToProcess[i]
        const fileData = fileDataMap.get(signature.fileId)

        if (!fileData) {
          errorCount++
          onNotifyError(`Не найден файл ${signature.fileName}`)
          continue
        }

        try {
          setDownloadProgress(`Подписание ${i + 1}/${signaturesToProcess.length}: ${signature.fileName} (${signature.signerTypeDisplay})`)

          const currentSignature = await onGetSignature(signature.signatureId, signature.signatureSource)
          const signatureData = await signBase64(selectedThumbprint, fileData, currentSignature.signature, selectedType?.id, comment)

          if (signatureData) {
            await onSubmitSignature(signature, selectedThumbprint, signatureData, currentSignature.rowVersion)
            successCount++
          }
        } catch (error) {
          errorCount++
          const isVersionError = (error as any)?.message?.includes('version') || (error as any)?.message?.includes('rowVersion')
          if (isVersionError) {
            versionErrorCount++
            onNotifyError(`Роль "${signature.signerTypeDisplay}" для файла "${signature.fileName}" уже была подписана`)
          } else {
            onNotifyError(`Ошибка подписания ${signature.fileName} (роль ${signature.signerTypeDisplay}): ${describeCryptoError(error)}`)
          }
        }
      }

      // Итоговое уведомление
      if (successCount > 0 && onNotifySuccess && errorCount === 0) {
        onNotifySuccess(`Успешно подписано ${successCount} документов`)
        onClose()
      } else if (successCount > 0 && errorCount > 0) {
        onNotifyError(`Подписано ${successCount} документов. Ошибок: ${errorCount} (из них конфликтов версий: ${versionErrorCount})`)
        onClose()
      } else if (errorCount > 0) {
        onNotifyError(`Не удалось подписать ни одного документа. Ошибок: ${errorCount}`)
      }

    } catch (e) {
      onNotifyError(`Ошибка при массовом подписании: ${describeCryptoError(e)}`)
    } finally {
      setIsSigning(false)
      setDownloadProgress('')
    }
  }

  const handleCertSelect = (thumbprint: string) => {
    setSelectedThumbprint(thumbprint)
  }
  const isBulkMode = !!signaturesToSignAll && signaturesToSignAll.length > 0

  if (!isBulkMode && !selectedSignature) return null

  // Получаем отфильтрованные подписи по выбранной роли
  const getFilteredSignatures = () => {
    if (!signaturesToSignAll) return []
    if (!selectedRole || selectedRole.id === 'all') return signaturesToSignAll
    return signaturesToSignAll.filter((sig) => sig.signerType === selectedRole.id)
  }

  const filteredSignaturesForSign = getFilteredSignatures()
  const canChooseMode = !isBulkMode && !!allowAttach && !!onAttachSignature
  const isAttachMode = canChooseMode && mode === 'attach'


  return (

    <Dialog
      title={isBulkMode ? "Массовое подписание документов" : "Подписание документа"}
      isOpen={isOpen}
      onClose={onClose}
      backdropClassName="standard"
      className={`${styles.smallDialog} ${styles.signDialog}`}
    >
      <div className={Classes.DIALOG_BODY}>
        <div className={styles.signItemContainer}>
          {/* ПРЕДУПРЕЖДЕНИЕ: если подписываем за другого человека */}
          {!isBulkMode && selectedSignerName && selectedSignerName !== selectedSignature?.signerTypeDisplay && (
            <div className={styles.warningBox}>
              <GeneralIcon
                type="general-warning"
                style={{
                  color: "#fbb360",
                  fontSize: "20px",
                  marginRight: "12px",
                  flexShrink: 0
                }}
              />
              <div className={styles.warningText}>
                <strong>Внимание!</strong> Вы подписываете документ за <strong>{selectedSignerName} </strong>(Доступно только для ГИП).
                {isAttachMode
                  ? ' Файл подписи будет добавлен к уже имеющимся подписям файла.'
                  : ' Убедитесь, что выбран правильный сертификат сотрудника.'}
              </div>
            </div>
          )}

          {/* Способ подписания за другого человека */}
          {canChooseMode && (
            <div className={styles.paramRow} style={{ marginBottom: "12px" }}>
              <div className={styles.paramName}>
                Способ подписания:
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <PlanrButton
                  type={mode === 'cert' ? 'blueish' : 'graybtn'}
                  size="small"
                  onClick={() => setMode('cert')}
                  disabled={isSigning || isAttaching}
                >
                  Сертификатом
                </PlanrButton>
                <PlanrButton
                  type={mode === 'attach' ? 'blueish' : 'graybtn'}
                  size="small"
                  title="Прикрепить готовый файл подписи стороннего подписанта (.sig)"
                  onClick={() => setMode('attach')}
                  disabled={isSigning || isAttaching}
                >
                  Файлом подписи
                </PlanrButton>
              </div>
            </div>
          )}

          {/* Инструкция по настройке КриптоПро */}
          {!isAttachMode && <div className={styles.instructionBox}>
            <GeneralIcon type="general-info" style={{ marginRight: '8px', flexShrink: 0 }} />
            <span>
              Для корректной работы подписи ознакомьтесь с{' '}
              <a
                href="https://docs.cryptopro.ru/cades/plugin/plugin-installation-windows"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.instructionLink}
              >
                инструкцией по настройке КриптоПро
              </a>
            </span>
          </div>}

          {/* Информация о документе (только для одиночной подписи) */}
          {!isBulkMode && selectedSignature && (
            <div className={styles.signInfo}>
              <div className={styles.signRow}>
                <span className={styles.label}>Договор:</span>
                <span className={styles.value}>
                  {selectedSignature.orderName}
                </span>
              </div>
              <div className={styles.signRow}>
                <span className={styles.label}>Раздел:</span>
                <span className={styles.value}>{selectedSignature.contentName}</span>
              </div>
              <div className={styles.signRow}>
                <span className={styles.label}>Файл:</span>
                <span className={styles.value}>
                  <FileLink
                    baseUrl={baseUrl}
                    file={{
                      fileId: selectedSignature.fileId,
                      fileName: selectedSignature.fileName
                    }}
                    onRemove={eat}
                    readOnly
                    style={{
                      whiteSpace: "normal",
                      fontSize: " 10px",
                      height: "auto",
                      padding: "8px 12px",
                      borderColor: "#ea561e",
                      color: "#ea561e"
                    }}
                  />
                  <span className={styles.version}> (версия {selectedSignature.version})</span>
                </span>
              </div>
              <div className={styles.signRow}>
                <span className={styles.label}>Роль:</span>
                <span className={`${styles.value} ${styles[selectedSignature.signerType]}`}>
                  {selectedSignature.signerTypeDisplay}
                </span>
              </div>
              <div className={styles.signRow}>
                <span className={styles.label}>Дата:</span>
                <span className={styles.value}>
                  {selectedSignature.date}
                </span>
              </div>
            </div>
          )}

          {isBulkMode && signaturesToSignAll && !cryptoLoading && !cryptoError && !isSigning && (
            <div className={styles.bulkInfo}>
              {/* Select для выбора роли */}
              <div className={styles.paramRow}>
                <div className={styles.paramName} style={{ width: "160px" }}>
                  Фильтр по роли:
                </div>
                <div style={{ width: "300px" }}>
                  <SimpleSelect
                    className={`full-width-select ${Classes.FILL}`}
                    activeItem={selectedRole}
                    items={availableRoles}
                    itemRenderer={renderSingleOption}
                    onItemSelect={(item: SelectItem) => {
                      setSelectedRole(item)
                    }}
                    popoverProps={{
                      rootBoundary: "viewport",
                    }}
                    inputProps={{
                      placeholder: "Поиск",
                    }}
                    filterable={false}
                    itemPredicate={filterItemPredicate}
                  >
                    <DefaultSelectedOption option={selectedRole} style={{ width: "300px" }} />
                  </SimpleSelect>
                </div>
              </div>

              <div className={styles.signRow}>
                <span className={styles.label} style={{ width: "180px" }}>Документов к подписи:</span>
                <span className={styles.value}>{filteredSignaturesForSign.length}</span>
              </div>


              <div className={styles.signRow}>
                <span className={styles.label}>Список файлов:</span>
                <div className={styles.fileList}>
                  {filteredSignaturesForSign.map((sig, idx) => (
                    <FileLink key={idx}
                      baseUrl={baseUrl}
                      file={{
                        fileId: sig.fileId,
                        fileName: sig.fileName
                      }}
                      onRemove={eat}
                      readOnly
                      style={{
                        whiteSpace: "normal",
                        fontSize: " 10px",
                        height: "auto",
                        borderColor: "#ea561e",
                        color: "#ea561e"
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {isAttachMode && selectedSignature && (
            <AttachSignatureForm
              key={attachFormKey}
              selectedSignature={selectedSignature}
              baseUrl={baseUrl}
              onNotifyError={onNotifyError}
              onNotifySuccess={onNotifySuccess}
              onChange={handleAttachFormChange}
            />
          )}

          {!isAttachMode && <div className={styles.cryptoSection}>
            {/* Статус загрузки/ошибки */}
            {cryptoLoading && (
              <div className={`${styles.cryptoStatus} ${styles.info}`}>
                <LocalLoader active={true} />
                <span>Загрузка сертификатов...</span>
              </div>
            )}

            {cryptoError && (
              <div className={`${styles.cryptoStatus} ${styles.error}`}>
                Ошибка: {cryptoError}
              </div>
            )}

            {/* Прогресс подписания */}
            {isSigning && downloadProgress && (
              <div className={`${styles.cryptoStatus} ${styles.info}`}>
                <LocalLoader active={true} />
                <span>{downloadProgress}</span>
              </div>
            )}

            {!cryptoLoading && !cryptoError && !isSigning && (
              <div>
                <div className={styles.paramRow}>
                  <div className={styles.paramName}>
                    Использование подписи:
                  </div>
                  <div style={{ width: "300px" }}>
                    <SimpleSelect
                      className={`full-width-select ${Classes.FILL}`}
                      activeItem={selectedType}
                      items={Constants.signatureTypeList}
                      itemRenderer={renderSingleOption}
                      onItemSelect={(i: SelectItem) => {
                        setSelectedType({ id: i.id, label: i.label })
                      }}
                      popoverProps={{
                        rootBoundary: "viewport",
                      }}
                      inputProps={{
                        placeholder: "Поиск",
                      }}
                      filterable={false}
                      itemPredicate={filterItemPredicate}
                    >
                      <DefaultSelectedOption option={selectedType} onClear={() => { setSelectedType(null) }} style={{ width: "300px" }} />
                    </SimpleSelect>
                  </div>
                </div>
                <div className={styles.paramRow} style={{ marginBottom: "20px" }}>
                  <div className={styles.paramName}>
                    Комментарий:
                  </div>
                  <InputGroup
                    value={comment}
                    onChange={(e: React.FormEvent<HTMLInputElement>) => {
                      setComment(e.currentTarget.value)
                    }}
                    className="planr-default-input"
                    style={{ marginRight: "16px", width: "300px" }}
                  />
                </div>
              </div>
            )}

            {/* Выбор сертификата */}
            {!cryptoLoading && !cryptoError && !isSigning && (
              <div className={styles.certificatesList}>
                <label className={styles.certLabel}>
                  Выберите сертификат для подписания:
                </label>

                {certs.length === 0 ? (
                  <div className={styles.noCertsWarning}>
                    <p>Сертификаты не найдены. Убедитесь, что:</p>
                    <ul>
                      <li>КриптоПро CSP установлен</li>
                      <li>КриптоПро плагин установлен и активирован</li>
                      <li>Сертификат установлен в личное хранилище</li>
                    </ul>
                    <PlanrButton
                      type="blueish"
                      size="small"
                      onClick={fetchCerts}
                      disabled={cryptoLoading}
                    >
                      Обновить список
                    </PlanrButton>
                  </div>
                ) : (
                  <div className={styles.certsGrid}>
                    {certs.map((cert) => {
                      const isSelected = selectedThumbprint === cert.thumbprint
                      return (
                        <div
                          key={cert.thumbprint}
                          className={`${styles.certItem} ${isSelected ? styles.selected : ''}`}
                          onClick={() => handleCertSelect(cert.thumbprint)}
                        >
                          <div className={styles.certIcon}>
                            <GeneralIcon
                              type={isSelected ? "general-checkbox" : "general-zero-checkbox"}
                              style={{ cursor: "pointer" }}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation()
                                handleCertSelect(cert.thumbprint)
                              }}
                            />
                          </div>
                          <div className={styles.certInfo}>
                            <div className={styles.certName}>
                              <strong>{cert.subjectName}</strong>
                            </div>
                            <div className={styles.certDate}>
                              Действует до: {new Date(cert.validTo).toLocaleDateString()}
                            </div>
                            <div className={styles.certIssuer}>
                              Издатель: {cert.issuerName}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>}
        </div>
      </div>

      <div className={Classes.DIALOG_FOOTER}>
        <Buttons
          left={
            <div className={styles.dialogButtons}>
              {isAttachMode ? (
                <PlanrButton
                  size="small"
                  type="greenish"
                  onClick={handleAttach}
                  disabled={!attachForm.sigBase64 || !attachForm.hashMatch || isAttaching}
                >
                  {isAttaching ? 'Загрузка...' : 'Прикрепить подпись'}
                </PlanrButton>
              ) : (
                <PlanrButton
                  size="small"
                  type="greenish"
                  onClick={isBulkMode ? handleSignAll : handleSign}
                  disabled={!selectedThumbprint || isSigning || cryptoLoading || certs.length === 0 || (isBulkMode && getFilteredSignatures().length === 0)}
                >
                  {isSigning ? 'Подписание...' : (isBulkMode && signaturesToSignAll ? `Подписать ${getFilteredSignatures().length > 0 ? `(${getFilteredSignatures().length})` : ''}` : 'Подписать')}
                </PlanrButton>
              )}
              <PlanrButton
                size="small"
                type="graybtn"
                onClick={onClose}
                disabled={isSigning || isAttaching}
              >
                Отмена
              </PlanrButton>
            </div>
          }
        />
      </div>
    </Dialog>

  )
})

export default SignDialog